"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, Copy } from "lucide-react";
import Link from "next/link";
import { type PropsWithChildren, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { copyToClipboard } from "../lib/copy-to-clipboard";
import { cn } from "../lib/cn";
import { trim } from "../lib/trim";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
export type ShortInternalLinkProps = {
  href: string;
  className: string;
  onClick: () => void;
  scroll?: boolean;
  children: ReactNode;
};
export type ShortProps = {
  text: string;
  link?: string | undefined;
  className?: string | undefined;
  length?: number | undefined;
  showCopy?: boolean | undefined;
  tooltipText?: string | undefined;
  linkClassName?: string | undefined;
  onInternalLinkClick?: (() => void) | undefined;
  scroll?: boolean | undefined;
  renderInternalLink?: ((props: ShortInternalLinkProps) => ReactNode) | undefined;
};
export function Short(t0) {
  const $ = _c(27);
  const {
    text,
    link,
    className,
    length,
    showCopy: t1,
    tooltipText,
    linkClassName,
    onInternalLinkClick,
    scroll,
    renderInternalLink
  } = t0;
  const showCopy = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== length || $[1] !== text) {
    t2 = trim(text, length);
    $[0] = length;
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const trimmedText = t2;
  const [showSuccess, setShowSuccess] = useState(false);
  const timeoutRef = useRef(null);
  let t3;
  if ($[3] !== text) {
    t3 = () => {
      copyToClipboard(text)().then(() => {
        setShowSuccess(true);
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          setShowSuccess(false);
        }, 2000);
      });
    };
    $[3] = text;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleCopy = t3;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== className) {
    t6 = cn("inline-flex items-center gap-1 whitespace-nowrap", className);
    $[7] = className;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = tooltipText ?? (trimmedText === text ? undefined : text);
  let t8;
  if ($[9] !== link || $[10] !== linkClassName || $[11] !== onInternalLinkClick || $[12] !== renderInternalLink || $[13] !== scroll || $[14] !== trimmedText) {
    t8 = <TrimmedText link={link} trimmedText={trimmedText} linkClassName={linkClassName} onInternalLinkClick={onInternalLinkClick} scroll={scroll} renderInternalLink={renderInternalLink} />;
    $[9] = link;
    $[10] = linkClassName;
    $[11] = onInternalLinkClick;
    $[12] = renderInternalLink;
    $[13] = scroll;
    $[14] = trimmedText;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <WithTooltip tooltipText={t7}>{t8}</WithTooltip>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== handleCopy || $[20] !== showCopy || $[21] !== showSuccess) {
    t10 = showCopy && <Tooltip><TooltipTrigger className="relative" asChild={true}><button type="button" onClick={handleCopy}><span className="sr-only">Copy</span>{showSuccess ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}</button></TooltipTrigger><TooltipContent><p className="text-xs">{showSuccess ? "Copied!" : "Copy"}</p></TooltipContent></Tooltip>;
    $[19] = handleCopy;
    $[20] = showCopy;
    $[21] = showSuccess;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t6 || $[25] !== t9) {
    t11 = <span className={t6}>{t9}{t10}</span>;
    $[23] = t10;
    $[24] = t6;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
function TrimmedText(t0) {
  const $ = _c(10);
  const {
    link,
    trimmedText,
    linkClassName,
    onInternalLinkClick,
    scroll,
    renderInternalLink
  } = t0;
  if (link === undefined) {
    return trimmedText;
  }
  let t1;
  let t2;
  if ($[0] !== link || $[1] !== linkClassName || $[2] !== onInternalLinkClick || $[3] !== renderInternalLink || $[4] !== scroll || $[5] !== trimmedText) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedClassName = cn("relative text-blue-500 hover:underline", linkClassName);
      if (link.startsWith("http")) {
        t2 = <a href={link} target="_blank" rel="noopener noreferrer" className={resolvedClassName}>{trimmedText}</a>;
        break bb0;
      }
      let t3;
      if ($[8] !== onInternalLinkClick) {
        t3 = () => {
          onInternalLinkClick?.();
        };
        $[8] = onInternalLinkClick;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      const internalLinkProps = {
        href: link,
        className: resolvedClassName,
        onClick: t3,
        children: trimmedText,
        ...(scroll !== undefined && {
          scroll
        })
      };
      if (renderInternalLink !== undefined) {
        t2 = renderInternalLink(internalLinkProps);
        break bb0;
      }
      t1 = <Link {...internalLinkProps} />;
    }
    $[0] = link;
    $[1] = linkClassName;
    $[2] = onInternalLinkClick;
    $[3] = renderInternalLink;
    $[4] = scroll;
    $[5] = trimmedText;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
}
function WithTooltip(t0) {
  const $ = _c(9);
  const {
    tooltipText,
    children
  } = t0;
  if (tooltipText === undefined) {
    let t1;
    if ($[0] !== children) {
      t1 = <span>{children}</span>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children) {
    t1 = <TooltipTrigger className="relative" asChild={true}><span>{children}</span></TooltipTrigger>;
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== tooltipText) {
    t2 = <TooltipContent>{tooltipText}</TooltipContent>;
    $[4] = tooltipText;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}